import React, { useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import { Droplet, Download, Share2, Instagram, Facebook, Globe, X } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const SOCIAL_HANDLE = 'aquapureus';
const WEBSITE = 'aquapureus.com';

export default function ShareImpactCard({ open, onClose, bottlesSaved, totalRefills }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const downloadAsImage = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
      });
      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `aquapure-impact-${Date.now()}.png`;
      link.href = dataUrl;
      link.click();
      toast.success('Image downloaded — share it on your social!');
    } catch (e) {
      console.error(e);
      toast.error('Could not download image');
    } finally {
      setBusy(false);
    }
  };

  const nativeShare = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
      });
      canvas.toBlob(async (blob) => {
        if (!blob) {
          setBusy(false);
          return;
        }
        const file = new File([blob], 'aquapure-impact.png', { type: 'image/png' });
        const shareData = {
          title: 'Aquapure US — Our Impact',
          text: `We've saved ${bottlesSaved.toLocaleString()} plastic bottles from landfills with Aquapure US! 💧 #refillrevolution`,
          files: [file],
        };
        if (navigator.canShare && navigator.canShare(shareData)) {
          try {
            await navigator.share(shareData);
            toast.success('Thanks for sharing our impact!');
          } catch (err) {
            if (err.name !== 'AbortError') toast.error('Share canceled');
          }
        } else {
          // Fallback: text-only share
          const fallback = {
            title: 'Aquapure US — Our Impact',
            text: shareData.text,
            url: `https://${WEBSITE}`,
          };
          if (navigator.share) {
            await navigator.share(fallback);
          } else {
            await navigator.clipboard.writeText(`${shareData.text}\nhttps://${WEBSITE}`);
            toast.success('Message copied — paste it anywhere!');
          }
        }
        setBusy(false);
      }, 'image/png');
    } catch (e) {
      console.error(e);
      toast.error('Could not prepare share');
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="share-impact-modal"
    >
      <div
        className="relative w-full max-w-md bg-card rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-card/80 hover:bg-muted flex items-center justify-center border border-border"
          data-testid="share-impact-close-btn"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* The shareable card itself */}
        <div
          ref={cardRef}
          className="relative p-8 bg-gradient-to-br from-[#0a4a4e] via-[#0d6e72] to-[#14a085] text-white"
          style={{ fontFamily: 'Space Grotesk, sans-serif' }}
        >
          {/* Decorative droplets */}
          <div className="absolute top-4 right-6 opacity-20">
            <Droplet className="w-20 h-20" />
          </div>
          <div className="absolute bottom-6 left-4 opacity-10">
            <Droplet className="w-14 h-14" />
          </div>

          <div className="relative z-10">
            {/* Logo + brand */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center border border-white/30">
                <Droplet className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="text-xl font-bold leading-tight">Aquapure US</div>
                <div className="text-xs text-white/80">Refill. Reduce. Reuse.</div>
              </div>
            </div>

            {/* Big number */}
            <div className="mb-2 text-sm uppercase tracking-widest text-white/80">
              Together we've saved
            </div>
            <div
              className="text-5xl font-black leading-none mb-1"
              data-testid="share-bottles-count"
            >
              {bottlesSaved.toLocaleString()}
            </div>
            <div className="text-lg font-semibold mb-6">
              plastic bottles from landfills
            </div>

            {/* Sub-stat */}
            <div className="flex items-center gap-3 mb-6 p-3 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <Droplet className="w-5 h-5 text-white/90" />
              <div className="text-sm">
                <span className="font-bold">{totalRefills.toLocaleString()}</span>
                <span className="text-white/80"> 5-gallon refills · 1 = 37 bottles saved</span>
              </div>
            </div>

            {/* Footer with QR + handles */}
            <div className="flex items-end justify-between gap-4">
              <div className="text-xs space-y-1">
                <div className="flex items-center gap-1.5">
                  <Instagram className="w-3.5 h-3.5" />
                  <span>@{SOCIAL_HANDLE}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Facebook className="w-3.5 h-3.5" />
                  <span>@{SOCIAL_HANDLE}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5" />
                  <span>{WEBSITE}</span>
                </div>
              </div>
              <div className="bg-white p-2 rounded-lg shadow-lg">
                <QRCodeSVG
                  value={`https://${WEBSITE}`}
                  size={84}
                  bgColor="#ffffff"
                  fgColor="#0a4a4e"
                  level="M"
                  includeMargin={false}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="p-4 bg-card flex flex-col gap-2">
          <p className="text-xs text-center text-muted-foreground mb-1">
            Snap a photo or share it directly to inspire others 💧
          </p>
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={nativeShare}
              disabled={busy}
              className="gap-2 bg-primary hover:bg-primary/90"
              data-testid="share-impact-share-btn"
            >
              <Share2 className="w-4 h-4" />
              Share
            </Button>
            <Button
              onClick={downloadAsImage}
              disabled={busy}
              variant="outline"
              className="gap-2"
              data-testid="share-impact-download-btn"
            >
              <Download className="w-4 h-4" />
              Download
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
